import { Camera, Globe, Music2, Play } from 'lucide-react'
import { Link } from 'react-router-dom'
import { BrandMark } from '../components/BrandMark'
import { LocaleSwitch } from '../components/LocaleSwitch'
import { ThemeToggle } from '../components/ThemeToggle'

const socialLinks = [
  {
    label: 'Facebook',
    href: 'https://www.facebook.com/miejscadolnegoslaska/',
    Icon: Globe,
  },
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/miejsca.dolnegoslaska/',
    Icon: Camera,
  },
  {
    label: 'TikTok',
    href: 'https://www.tiktok.com/@miejscadolnegoslaska',
    Icon: Music2,
  },
  {
    label: 'YouTube',
    href: 'https://www.youtube.com/watch?v=1R_adCyXDl0',
    Icon: Play,
  },
] as const

type FooterCol = {
  header: string
  items: ReadonlyArray<readonly [label: string, href: string, external?: boolean]>
}

const columns: FooterCol[] = [
  {
    header: 'Akcja',
    items: [
      ['O projekcie', '/o-projekcie'],
      ['Aktualności', '/o-projekcie/aktualnosci'],
      ['Ranking', '/o-projekcie/ranking'],
      ['FAQ', '/#faq'],
      ['Miejsca', '/miejsca'],
      ['Społeczność', '/spolecznosc'],
    ],
  },
  {
    header: 'Konto',
    items: [
      ['Logowanie', '/login'],
      ['Społeczność', '/spolecznosc'],
    ],
  },
  {
    header: 'Wsparcie',
    items: [
      ['Kontakt', 'mailto:kontakt@miejscadolnegoslaska.pl', true],
      ['Regulamin', '/regulamin'],
      ['Polityka prywatności', '/polityka-prywatnosci'],
      ['Usuń konto', '/usun-konto'],
      ['Regulamin kanału społecznościowego', '/regulamin-kanalu-spolecznosciowego'],
    ],
  },
]

/** SEO / dev links — not in main footer columns */
const technicalLinks: ReadonlyArray<readonly [label: string, href: string, external?: boolean]> =
  [
    ['Discover (EN)', '/discover'],
    ['Porównanie', '/porownanie'],
    ['llms.txt', '/llms.txt', true],
    ['GitHub', 'https://github.com/Zuz2137/PaszportDolnegoSlaska', true],
  ]

export function FooterSection() {
  return (
    <footer className="border-t border-ink/10 bg-paper">
      <div className="container-x py-16 md:py-20">
        <div className="content grid grid-cols-1 gap-12 md:grid-cols-12 md:gap-16">
          <div className="md:col-span-6">
            <BrandMark
              title="Paszport Dolnego Śląska 2026"
              subtitle="by Miejsca Dolnego Śląska"
            />
            <p className="mt-5 max-w-md text-base font-medium text-ink/55">
              12 miejsc, 12 historii, 12 miesięcy. Akcja promująca region Dolnego Śląska
              i jego mniej znane zakątki.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-2">
              {socialLinks.map(({ href, label, Icon }) => (
                <a
                  key={href}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-ink/5 text-ink/65 transition hover:bg-ink/10 hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/60"
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                </a>
              ))}
            </div>
          </div>

          <nav
            aria-label="Stopka"
            className="grid grid-cols-2 gap-8 md:col-span-6 md:grid-cols-3 md:gap-10"
          >
            {columns.map((col) => (
              <div key={col.header}>
                <div className="text-base font-extrabold text-ink">{col.header}</div>
                <ul className="mt-4 space-y-2">
                  {col.items.map(([label, href, external]) => (
                    <li key={`${col.header}-${href}`}>
                      {external ? (
                        <a
                          href={href}
                          className="text-base font-medium text-ink/55 transition hover:text-ink"
                        >
                          {label}
                        </a>
                      ) : href.startsWith('/#') || href.startsWith('#') ? (
                        <a
                          href={href}
                          className="text-base font-medium text-ink/55 transition hover:text-ink"
                        >
                          {label}
                        </a>
                      ) : (
                        <Link
                          to={href}
                          className="text-base font-medium text-ink/55 transition hover:text-ink"
                        >
                          {label}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </div>

        <div className="mt-12 flex flex-col-reverse items-start justify-between gap-4 border-t border-ink/10 pt-6 text-xs font-semibold text-ink/45 md:flex-row md:items-center">
          <div className="flex flex-col gap-2">
            <span>
              © {new Date().getFullYear()} Miejsca Dolnego Śląska. Wszelkie prawa zastrzeżone.
            </span>
            <details className="group text-[11px] font-medium text-ink/35">
              <summary className="cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                <span className="underline decoration-ink/20 underline-offset-2 group-open:no-underline">
                  Dokumentacja techniczna
                </span>
              </summary>
              <ul className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
                {technicalLinks.map(([label, href, external]) => (
                  <li key={href}>
                    {external ? (
                      <a href={href} className="hover:text-ink/55">
                        {label}
                      </a>
                    ) : (
                      <Link to={href} className="hover:text-ink/55">
                        {label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </details>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <ThemeToggle size="sm" />
            <LocaleSwitch size="sm" />
          </div>
        </div>
      </div>
    </footer>
  )
}
